import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Plus, Calendar, Users, Anchor, Trash2, Loader2, Waves, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { plannerApi } from './plannerApi';
import PwaInstallBanner from '../../components/PwaInstallBanner';
import { viewerStore } from './viewerStore';

const PlannerHome = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const api = plannerApi(token);
  const [me, setMe] = useState(null);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: '', start_date: '', end_date: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    // Viewer-mode fast path: divers who have visited share links should land
    // on their saved schedules instead of the auth screen / operator gate.
    if (!token && viewerStore.hasAny()) {
      navigate('/planner/viewer', { replace: true });
      return;
    }
    if (!token) {
      navigate('/auth?redirect=/planner');
      return;
    }
    let active = true;
    (async () => {
      try {
        const m = await api.me();
        if (!active) return;
        setMe(m);
        if (!m.is_operator) {
          // Logged-in non-operator with saved shares → viewer.
          if (viewerStore.hasAny()) {
            navigate('/planner/viewer', { replace: true });
            return;
          }
          setLoading(false);
          return;
        }
        const t = await api.listTrips();
        if (active) setTrips(t);
      } catch (e) {
        toast.error('Failed to load planner');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line
  }, [token]);

  const submit = async () => {
    if (!form.name || !form.start_date || !form.end_date) {
      toast.error('Name and dates are required');
      return;
    }
    setCreating(true);
    try {
      const trip = await api.createTrip(form);
      toast.success('Trip created');
      navigate(`/planner/trips/${trip.id}`);
    } catch (e) {
      toast.error('Failed to create trip');
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this trip?')) return;
    try {
      await api.deleteTrip(id);
      setTrips((p) => p.filter((t) => t.id !== id));
      toast.success('Deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" />
      </div>
    );
  }

  if (!user || !me?.is_operator) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="bg-white rounded-xl shadow-sm border max-w-md w-full p-8 text-center">
          <Waves className="w-12 h-12 mx-auto text-[#FF6B35] mb-4" />
          <h2 className="text-xl font-bold text-[#0A2342] mb-2">Dive Planner</h2>
          <p className="text-sm text-gray-600 mb-4">
            The Dive Planner is for verified dive operators and trip leaders. Please contact Just Dive to request operator access for your account ({me?.email || 'your account'}).
          </p>
          <Link to="/" className="text-[#FF6B35] hover:underline text-sm">← Back to site</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="planner-home">
      <div className="bg-gradient-to-br from-[#0A2342] to-[#1a3a5c] text-white px-5 pt-10 pb-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Waves className="w-7 h-7 text-[#FF6B35]" />
            <h1 className="text-2xl md:text-3xl font-bold">Dive Planner</h1>
            <Link to="/planner/manual" className="ml-auto text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg flex items-center gap-1" data-testid="manual-link">
              <BookOpen className="w-3.5 h-3.5" /> User Manual
            </Link>
          </div>
          <p className="text-sm opacity-80">Hi {user?.first_name || 'there'} — manage your trips and daily launches.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 -mt-6">
        <div className="bg-white rounded-xl shadow-sm border p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-[#0A2342]">Your trips</h2>
            <p className="text-xs text-gray-500">{trips.length} trip{trips.length === 1 ? '' : 's'}</p>
          </div>
          <Button onClick={() => setShowNew(true)} className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 w-full sm:w-auto" data-testid="new-trip-btn">
            <Plus className="w-4 h-4 mr-2" /> New Trip
          </Button>
        </div>

        {trips.length === 0 ? (
          <div className="bg-white rounded-xl border p-10 text-center">
            <Anchor className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">No trips yet. Tap "New Trip" to plan your first one.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {trips.map((t) => (
              <Link key={t.id} to={`/planner/trips/${t.id}`} className="block bg-white rounded-xl border p-4 hover:border-[#FF6B35] transition" data-testid={`trip-card-${t.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-[#0A2342] truncate">{t.name}</h3>
                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                      <Calendar className="w-3 h-3" /> {t.start_date} → {t.end_date}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {t.divers?.length || 0} divers</span>
                      <span className="flex items-center gap-1"><Anchor className="w-3 h-3" /> {t.crew?.length || 0} crew</span>
                      <span>{t.daily_plans?.length || 0} days planned</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.preventDefault(); remove(t.id); }}
                    className="text-gray-300 hover:text-red-500 p-1"
                    aria-label="Delete trip"
                    data-testid={`delete-trip-${t.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Trip</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Trip name (e.g. Aliwal Shoal — May)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-trip-name" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Start</label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="new-trip-start" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">End</label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="new-trip-end" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button onClick={submit} disabled={creating} className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90" data-testid="new-trip-submit">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PwaInstallBanner />
    </div>
  );
};

export default PlannerHome;
